import React, { useState, useEffect } from 'react';
import '../css/NavBar.css';
import { useTabStore, useAppStore, useTranslation, useDialogStore, useSOWDataStore, useSOWMessagesStore, useUpdateStore, selectUpdateAvailable } from '../store';
import { SettingsDialog } from './dialogs/SettingsDialog';
import { QRScanner } from './dialogs/QRScanner';
import { CONSTANTS } from '../constants/constants';
import MessageNotificationIcon from './MessageNotificationIcon';
import SignatureNotificationIcon from './SignatureNotificationIcon';
import SyncQueueBadge from './SyncQueueBadge';
import { isSignatureRequired, isValueEmpty } from '../services/utils';
import { queryParamsService } from '../services/queryParams';
import { useSignatureStore } from '../store/useSignatureStore';

const NavBar = () => {
    const { t } = useTranslation();
    const { isOnline, setupNetworkListeners } = useAppStore();
    const { openDialog } = useDialogStore();
    const { activeTab, setActiveTab } = useTabStore();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isMobileView, setIsMobileView] = useState(false);
    const { sowData, getSOWName, fetchSOWData } = useSOWDataStore();
    const { hasNewMessages, startPolling } = useSOWMessagesStore();
    const updateAvailable = useUpdateStore(selectUpdateAvailable);
    const { jobsSignatures, fetchSignatureData, hasHydrated } = useSignatureStore();
    const { issueKey } = queryParamsService.getParams();

    const requiresPickupSignature =
      isSignatureRequired(sowData?.scopeOfWork?.requiresPickupSignature);
    const signatureData = jobsSignatures[issueKey];
    const isPickupSignatureSubmitted =
      signatureData?.status === CONSTANTS.STATUS.COMPLETED && Boolean(signatureData?.signature);
    const showPickupSignatureNotification = requiresPickupSignature && !isPickupSignatureSubmitted;

    useEffect(() => {
      fetchSOWData();
    }, [fetchSOWData]);

    useEffect(() => {
      if (!hasHydrated || !requiresPickupSignature) {
        return;
      }
      fetchSignatureData(true);
    }, [hasHydrated, requiresPickupSignature, fetchSignatureData]);

    useEffect(() => {
      startPolling();
    }, [startPolling]);

    useEffect(() => {

        const handleHashChange = () => {
          const newHash = window.location.hash.replace('#', '');
          setActiveTab(newHash || 'home');
        };
        window.addEventListener('hashchange', handleHashChange);
        return () => window.removeEventListener('hashchange', handleHashChange);
    });
    
    useEffect(() => {
      const cleanup = setupNetworkListeners();
      
      // Sync with URL hash on component mount
      const hash = window.location.hash.substring(1);
      if (hash && hash !== activeTab) {
        setActiveTab(hash);
      } else if (!hash && !activeTab) {
        setActiveTab(CONSTANTS.TABS.HOME);
      }
      
      // Check if we're on a mobile device
      const checkIfMobile = () => {
        setIsMobileView(window.innerWidth <= 768);
      };
      
      // Initial check
      checkIfMobile();
      
      // Add event listener for window resize
      window.addEventListener('resize', checkIfMobile);
      
      return () => {
        cleanup();
        window.removeEventListener('resize', checkIfMobile);
      };
    }, [setupNetworkListeners]);

    const handleSettings = (e) => {
      e.preventDefault();
      openDialog(SettingsDialog, {});
    };
    
    const handleQRScanner = (e) => {
      e.preventDefault();
      openDialog(QRScanner, {});
    };
    
    const getSOWKey = () => {
        let name = getSOWName();
        if (isValueEmpty(name)) {
            name = issueKey;
        }
        return name;
    };
    
    const handleTabClick = (tabName, e) => {
      e.preventDefault();
      switchTab(tabName);
    };
    
    const switchTab = (tabName) => {
      setActiveTab(tabName);
      // Update URL hash as well
      window.location.hash = tabName;
      
      // Close mobile menu after clicking a link
      if (isMobileView) {
        setIsMobileMenuOpen(false);
      }
    };
    
    const isTabActive = (tabName) => {
      return activeTab === tabName ? 'active' : '';
    };

    const toggleMobileMenu = () => {
      setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    // Function to get the display name of the current tab
    const getCurrentTabName = () => {
      switch(activeTab) {
        case CONSTANTS.TABS.HOME:
          return t('tabs.home');
        case CONSTANTS.TABS.DESCRIPTION:
          return t('tabs.description');
        case CONSTANTS.TABS.COMPONENTS:
          return t('tabs.components');
        case CONSTANTS.TABS.LOCATION:
          return t('tabs.location');
        case CONSTANTS.TABS.TASKS:
          return t('tabs.tasks');
        case CONSTANTS.TABS.MESSAGES:
          return t('tabs.messages');
        default:
          return t('tabs.home');
      }
    };

    return (
        <div className="navbar-container">
            <div className="logo" title={t('Hypertec')}>
                <div className="app-name">{getSOWKey()}</div>
                <div className={`app-status glyphicon glyphicon-flash ${isOnline ? 'green-text' : 'red-text'}`} title={t(isOnline ? 'statusConnected' : 'statusOffline')}></div>
                <SyncQueueBadge />
            </div>
            
            {/* Mobile header showing current tab - only visible on mobile */}
            {isMobileView && (
              <div className="mobile-current-tab">
                {getCurrentTabName()}
              </div>
            )}
            
            {/* Mobile menu button - only visible on mobile */}
            <div className="mobile-menu-toggle" onClick={toggleMobileMenu}>
                <span></span>
                <span></span>
                <span></span>
            </div>
            
            <div className="spacer"></div>
            <nav className={`navbar navbar-inverse ${isMobileMenuOpen ? 'mobile-menu-open' : ''}`}>
                <ul className="nav navbar-nav">
                    <li className={isTabActive(CONSTANTS.TABS.HOME)}><a href="#home" onClick={(e) => handleTabClick(CONSTANTS.TABS.HOME, e)}>{t('tabs.home')}</a></li>
                    <li className={isTabActive(CONSTANTS.TABS.DESCRIPTION)}><a href="#description" onClick={(e) => handleTabClick(CONSTANTS.TABS.DESCRIPTION, e)}>{t('tabs.description')}</a></li>
                    <li className={isTabActive(CONSTANTS.TABS.COMPONENTS)}><a href="#components" onClick={(e) => handleTabClick(CONSTANTS.TABS.COMPONENTS, e)}>{t('tabs.components')}<SignatureNotificationIcon isVisible={showPickupSignatureNotification}/></a></li>
                    <li className={isTabActive(CONSTANTS.TABS.LOCATION)}><a href="#location" onClick={(e) => handleTabClick(CONSTANTS.TABS.LOCATION, e)}>{t('tabs.location')}</a></li>
                    <li className={isTabActive(CONSTANTS.TABS.TASKS)}><a href="#tasks" onClick={(e) => handleTabClick(CONSTANTS.TABS.TASKS, e)}>{t('tabs.tasks')}</a></li>
                    <li className={isTabActive(CONSTANTS.TABS.MESSAGES)}><a href="#messages" onClick={(e) => handleTabClick(CONSTANTS.TABS.MESSAGES, e)}>{t('tabs.messages')}<MessageNotificationIcon isVisible={hasNewMessages}/></a></li>
                </ul>
                <ul className="nav navbar-nav navbar-right">
                    <li><a onClick={handleQRScanner} href="#qr"><span className="glyphicon glyphicon-qrcode"></span> {t('qr')}</a></li>          
                    <li><a onClick={handleSettings} href="#settings"><span className="glyphicon glyphicon-cog"></span> {t('settings')}<SignatureNotificationIcon isVisible={updateAvailable}/></a></li>
                </ul>
            </nav>
        </div>
    );
};

export default NavBar;